/**
 * HTTP клиент для BeepApp API.
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "/api"

let authToken: string | null = null

export function setAuthToken(token: string | null) {
  authToken = token
  if (token) {
    localStorage.setItem("bibi_token", token)
  } else {
    localStorage.removeItem("bibi_token")
  }
}

export function getAuthToken(): string | null {
  if (!authToken) {
    authToken = localStorage.getItem("bibi_token")
  }
  return authToken
}

interface RequestOptions {
  method?: string
  body?: unknown
  headers?: Record<string, string>
  cache?: RequestCache
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public detail: string,
    public errorCode?: string,
  ) {
    super(detail)
    this.name = "ApiError"
  }
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const token = getAuthToken()
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...options.headers,
  }

  if (token) {
    headers["Authorization"] = `Bearer ${token}`
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: options.method || "GET",
    headers,
    body: options.body ? JSON.stringify(options.body) : undefined,
    cache: options.cache,
  })

  if (!response.ok) {
    let detail = `HTTP ${response.status}`
    let errorCode: string | undefined

    try {
      const errorData = await response.json()
      if (typeof errorData.detail === "string") {
        detail = errorData.detail
      } else if (errorData.detail?.message) {
        detail = errorData.detail.message
        errorCode = errorData.detail.error_code
      }
    } catch {
      // ignore parse error
    }

    throw new ApiError(response.status, detail, errorCode)
  }

  if (response.status === 204) {
    return undefined as T
  }

  return response.json()
}

async function uploadRequest<T>(path: string, formData: FormData): Promise<T> {
  const token = getAuthToken()
  const headers: Record<string, string> = {}

  if (token) {
    headers["Authorization"] = `Bearer ${token}`
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: "POST",
    headers,
    body: formData,
  })

  if (!response.ok) {
    let detail = `HTTP ${response.status}`
    let errorCode: string | undefined

    try {
      const errorData = await response.json()
      if (typeof errorData.detail === "string") {
        detail = errorData.detail
      } else if (errorData.detail?.message) {
        detail = errorData.detail.message
        errorCode = errorData.detail.error_code
      }
    } catch {
      // ignore parse error
    }

    throw new ApiError(response.status, detail, errorCode)
  }

  if (response.status === 204) {
    return undefined as T
  }

  return response.json()
}

export function getApiBaseUrl(): string {
  return API_BASE_URL
}

export const api = {
  get: <T>(path: string, options?: Pick<RequestOptions, "headers" | "cache">) => request<T>(path, options),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: "POST", body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: "PUT", body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: "PATCH", body }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
  upload: <T>(path: string, formData: FormData) => uploadRequest<T>(path, formData),
}
